import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Attack,
  Character,
  CharacterAbility,
  CharacterAction,
  CharacterSpell,
  CharacterSheetInterface,
  CharacterWeapon,
  SkillAsAbility,
  SpellsFromList,
  StoredSpell,
  SpellsFromListWithCosts,
  CharacterFeature,
} from "@dndModels";
import { PALETTE } from "@dndModels/Constants";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DataUtilities from '@dndUtilities/data_utilities';
import { makeStyles } from "@utilities/theme_utilities";

import CharacterEffects from "@dndComponents/character/spells_and_abilities/effects/CharacterEffects";

import ExpandButton from "@components/input/buttons/ExpandButton";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import { SpellLevel } from '@dndModels/Types';
// import ViewSpell from '../../display/ViewSpell';
// import ViewItem from '../../display/ViewItem';

interface Props {
  char_int: CharacterSheetInterface;
  char?: Character;
  level?: SpellLevel;
  show_casting_time?: boolean;
  casting_time_cost?: boolean;
  action: CharacterAction;
  group?: string;
  processing?: boolean;
  shared?: boolean;
  onOpen: () => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const data_util = DataUtilities.getInstance();

export default function CharacterActionInput({
  char_int,
  char,
  action,
  processing = false,
  shared = false,
  show_casting_time = false,
  casting_time_cost = false,
  onOpen,
  level = -1,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [recalcDisabled, setRecalcDisabled] = useState(false);
  const [is_processing, setProcessing] = useState(false);
  const [ping, setPing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // eslint-disable-next-line
  useEffect(() => {
    if (!fetched) {
      char_int.subscription_holder.subscribe("Resources", mounted, resourcesChanged);
      // console.log(id);
      setFetched(true);
    }
  });

  const resourcesChanged = () => {
    // console.log("Action Input");
    setRecalcDisabled(true);
  }

  useEffect(() => {
    if (is_processing !== processing) {
      setProcessing(processing);
    }
    // eslint-disable-next-line
  }, [processing]);
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  useEffect(() => {
    if (recalcDisabled) {
      setRecalcDisabled(false);
    }
  }, [recalcDisabled, setReloading]);

  const useStyles = makeStyles()((theme) => {
    let backgroundColor = theme.palette.secondary.main;
    const color: string | undefined = "#FFFFFF";
    // if (action.name.includes("Arms of Hadar")) {
    //   debugger;
    // }
    if (action.action instanceof CharacterWeapon || action.action instanceof Attack) {
      const colors = char_util.getColors({ char: char_int, user: api.user, for_id: "Attacks" });
      backgroundColor = colors.backgroundColor;
      // color = colors.color;
    } else if (action.action instanceof CharacterAbility || action.action instanceof CharacterSpell) {
      const cfss = char_util.getCFSSForCF(char_int, action);
      if (cfss.length === 1) {
        const colors = char_util.getColors({ char: char_int, user: api.user, for_id: cfss[0].source_id });
        backgroundColor = colors.backgroundColor;
        // color = colors.color;
      } else if (cfss.length > 1) {
        for (const cfs of cfss) {
          const colors = char_util.getColors({ char: char_int, user: api.user, for_id: cfs.source_id });
          
          backgroundColor = colors.backgroundColor;
          // color = colors.color;
          
          if (backgroundColor !== PALETTE.secondary.main || color !== PALETTE.secondary.light) {
            break;
          }
        }
      }
    }
    // if (color === "Default") {
    //   color = undefined;
    // }
    return ({
      root: {
        // margin: "4px",
        backgroundColor,
        color
      },
      upgraded: {
        color: "white"
      },
    });
  });

  const the_action = action.action;
  const enabled = api.user && 
    (char_int.owner_id === api.user._id || 
      (char_int instanceof Character && char_int.campaign && char_int.campaign.owner_id === api.user._id));

  const renderName = () => {
    let name = action.getName(char_int);
    let extra: any = null;
    let extra2: any = null;
    let extra3: any = null;
    let bonus: any = null;
    // if (name.includes("Booming")) {
    //   console.log(name);
    //   console.log(action);
    // }
    if (the_action instanceof Attack) {
      name = "Unarmed Strike";
    } else if (the_action instanceof CharacterAbility) {
      if (the_action.the_spell) {
        // SpellAsAbility and SpellsFromList, put the_action.name underneath in smaller_label
        name = action.getName(char_int);
        extra = (
          <>
            { the_action.concentration && <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>C</span> }
            { (the_action.concentration && the_action.ritual) && <>&nbsp;</> }
            { !action.no_ritual && the_action.ritual && <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>R</span> }
            { (the_action.concentration || the_action.ritual) && <>&nbsp;</> }
            { <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>{ the_action.components.join(" ") }</span> }
          </>
        );
        if ((the_action.the_ability instanceof SpellsFromList || 
          the_action.the_ability instanceof SpellsFromListWithCosts) && 
          the_action.name !== name) {
          extra2 = (
            <Grid
              className="small_label gray"
              size={12}>{ the_action.name }</Grid>
          );
        }
      } else {
        name = action.getName(char_int);
      }
    } else if (the_action instanceof CharacterSpell) {
      name = action.getName(char_int);
      extra = (
        <>
          { the_action.concentration && <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>C</span> }
          { (the_action.concentration && the_action.ritual) && <>&nbsp;</> }
          { !action.no_ritual && the_action.ritual && <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>R</span> }
          { (the_action.concentration || the_action.ritual) && <>&nbsp;</> }
          { <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>{ the_action.components.join(" ") }</span> }
        </>
      );
      // if (the_action.source_type === "Class") {
      //   const char_class = char.spirit.classes.find(o => o.true_id === the_action.source_true_id);
      //   if (char_class) {
      //     const bs = char_class.bonus_spells.find(o => o.ff && o.ff.spell_ids.includes(the_action.spell_id));
      //     if (bs && bs.ff) {
      //       bonus = (
      //         <Grid
      //           className="small_label gray"
      //           size={{
      //             xs: 12
      //           }}>
      //           Bonus Spell from { bs.ff.name }
      //         </Grid>
      //       );
      //     }
      //   }
      // }
    } else if (the_action instanceof StoredSpell && char_int instanceof Character) {
      name = action.getName(char_int);
      const item = char_int.inventory.items.find(o => o.true_id === the_action.item_id);
      extra2 = (
        <Grid
          className="small_label gray"
          size={12}>
          Stored Spell on { item?.name??"" }
        </Grid>
      );
      extra = (
        <>
          { the_action.concentration && <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>C</span> }
          { (the_action.concentration) && <>&nbsp;</> }
          { <span style={{ backgroundColor: "black", color: "white", fontSize: "8px" }}>{ the_action.components.join(" ") }</span> }
        </>
      );
    } else if (the_action) {
      name = action.getName(char_int);
    } else {
      name = action.getName(char_int);
    }
    if (action.action instanceof CharacterAbility || action.action instanceof CharacterSpell) {
      const cfs = char_util.getCFSForCF(char_int, action);
      if (cfs) {
        extra3 = (
          <Grid
            className="small_label gray"
            size={12}>
            { char_util.getUseName(cfs) }
          </Grid>
        );
      }
    }
    if (show_casting_time) {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            <span>{ name }&nbsp;{ extra }</span>
          </Grid>
          { extra2 }
          { extra3 }
          <Grid
            className="small_label gray"
            size={12}>
            <b>Casting Time: </b>{ action.casting_time === "Special" ? action.special_casting_time : action.casting_time }
          </Grid>
          { action.duration !== "Instantaneous" && 
            <Grid
              className="small_label gray"
              size={12}>
              <b>Duration: </b><em style={{ fontStyle: "normal" }}>{ action.duration }</em>
            </Grid>
          }
          { bonus }
        </Grid>
      );
    } else if (extra) {
      if (extra2 || extra3) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <span>{ name }&nbsp;{ extra }</span>
            </Grid>
            { extra2 }
            { extra3 }
            { action.duration !== "Instantaneous" && 
              <Grid
                className="small_label gray"
                size={12}>
                <b>Duration: </b><em style={{ fontStyle: "normal" }}>{ action.duration }</em>
              </Grid>
            }
            { bonus }
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            <Grid size={12}>{ name }&nbsp;{ extra }</Grid>
            { action.duration !== "Instantaneous" && 
              <Grid
                className="small_label gray"
                size={12}>
                <b>Duration: </b><em style={{ fontStyle: "normal" }}>{ action.duration }</em>
              </Grid>
            }
            { bonus }
          </Grid>
        );
      }
    } else {
      return (
        <Grid container spacing={0} direction="row">
          <Grid size={12}>
            { name }
          </Grid>
          { extra2 }
          { extra3 }
          { action.duration !== "Instantaneous" && 
            <Grid
              className="small_label gray"
              size={12}>
              <b>Duration: </b><em style={{ fontStyle: "normal" }}>{ action.duration }</em>
            </Grid>
          }
          { bonus }
        </Grid>
      );
    }
  }

  const hasExtraEffects = () => {
    if (action.extra_actions.length > 0) {
      return true;
    } else {
      const obj = action.action;
      if (obj instanceof CharacterAbility && obj.the_ability instanceof SkillAsAbility) {
        return obj.the_ability.skill_ids.length > 1;
      } else if (obj instanceof CharacterAbility || obj instanceof CharacterSpell || obj instanceof StoredSpell) {
        if (obj.effects.length > 1) {
          return true;
        } else {
          const extra_effects = char_util.getExtraEffects({ char: char_int, obj, action });
          // const extra_effects_by_type = char_util.getExtraEffectsByType(char, character, obj);
          return extra_effects.length > 0; // || extra_effects_by_type.length > 0;
        }
      } else if (obj instanceof CharacterWeapon) {
        // if (action.name.includes("Dagger") && action.casting_time === "A") {
        //   console.log(action);
        // }
        let attacks = [...obj.attacks];
        if (attacks.filter(o => o.attack_type.includes("Light")) && char_int instanceof Character) {
          if (action.casting_time === "Attack") {
            if (char_int.c_options.light_melee_action === "Bonus Action" || obj.equipped_hand === "Main") {
              attacks = attacks.filter(o => !o.attack_type.includes("Light"));
            }
          } else if (char_int.c_options.light_melee_action === "Bonus Action" || obj.equipped_hand === "Off") {
            attacks = attacks.filter(o => o.attack_type.includes("Light"));
          }
        }
        if (attacks.length > 1) {
          return true;
        } else if (obj.ammunition_type !== "None" && char_int instanceof Character &&
          char_int.inventory.ammunitions.filter(o => 
            o.ammunition_type === obj.ammunition_type && 
            o.count > 0).map(o => o.pseudo_item_id).length > 1) {
          return true;
        } else {
          const extra_effects = char_util.getExtraEffects({ char: char_int, obj }); // , undefined, obj.name.includes("Crossbow"));
          return extra_effects.length > 0;
        }
      } else if (obj instanceof Attack) {
        const extra_effects = char_util.getExtraEffects({ char: char_int, obj });
        return extra_effects.length > 0;
      }
      return false;
    }
  }

  /**
   * Range, Targets, Attack/DC, Effect
   */
  const renderEffects = () => {
    // if (action.name.includes("Tricks") && action.action instanceof CharacterAbility) {
    //   debugger;
    // }
    if (action.action) {
      // if (action.action instanceof Attack) {
      //   debugger;
      // }
      const attack_mod = action.attack_mod > 0 ? action.attack_mod : 
        (action.action instanceof Attack && action.action.attack_rolls.length > 0 ? 
          char_int.mind.current_ability_scores.getModifier(action.action.attack_rolls[0].ability_score) : 
          (action.action instanceof CharacterWeapon && action.action.attacks.length > 0 && action.action.attacks[0].attack_rolls.length > 0 ? 
            char_int.mind.current_ability_scores.getModifier(action.action.attacks[0].attack_rolls[0].ability_score) : 0));
      return (
        <CharacterEffects 
          char_int={char_int}
          char={char}
          obj={action.action}
          action={action}
          level={level}
          // wide
          attack_mod={attack_mod}
          casting_time={action.casting_time}
          processing={processing}
          disabled={!enabled && !shared}
          shared={shared}
          // log_it={!(action.action instanceof Attack) && action.action.name.includes("Animal Friend")}
          show_extra_effects={expanded}
          include_cast_button
          casting_time_cost={casting_time_cost}
          onChange={(change_types: string[]) => {
            if (change_types.length > 0) {
              setPing(!ping);
              char_int.changed();
              char_int.subscription_holder.recalculate();
            }
          }}
        />
      );
    }
    return "";
  }

  const renderDisabled = () => {
    // if (action.name.includes("Delerium")) {
    //   debugger;
    // }
    if (action.feature_disabled) {
      let source_true_id = action.feature_disabled.source_true_id;
      const cfs = char_int.char_feature_sources.find(o => o.true_id === source_true_id);
      let source_name = "";
      if (cfs) {
        source_name = cfs.name;
      } else {
        source_true_id = action.feature_disabled.source_id;
        // const cond = char.conditions.find(o => o.tr).condition_hash[source_true_id];
        const condition = data_util.conditions.find(o => o._id === source_true_id);
        if (condition) {
          source_name = condition.name;
        }
      }
      if (source_name !== "") {
        return (
          <div className="small_label gray">
            Disabled from { source_name }
          </div>
        );
      }
    }
    return null;
  }

  const { classes, cx } = useStyles();
  return (
    <div className="padded">
      <Grid container spacing={0} direction="row"
        className={cx(classes.root)}>
        <Grid
          size={{
            xs: 2
          }}>
          <div className="centered clickable"
            onClick={onOpen}>
            { renderName() }
          </div>
          { (the_action instanceof CharacterAbility || the_action instanceof CharacterSpell || the_action instanceof StoredSpell) && the_action.can_be_active && 
            <ToggleButtonBox
              name="Active"
              value={the_action.active}
              onToggle={() => {
                const obj = the_action;
                if (obj instanceof CharacterAbility || obj instanceof CharacterSpell || obj instanceof StoredSpell) {
                  obj.active = !obj.active;
                  char_int.changed();
                  char_int.subscription_holder.recalculate();
                  setPing(!ping);
                }
              }}
            />
          }
          { !recalcDisabled && renderDisabled() }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 1
          }}>
          { hasExtraEffects() &&
            <Grid
              size={{
                xs: 6
              }}>
              <ExpandButton 
                is_expanded={expanded}
                onClick={() =>{
                  setExpanded(!expanded);
                }}
              />
            </Grid>
          }
        </Grid>
        <Grid
          size={{
            xs: 9
          }}>{ !reloading && renderEffects() }</Grid>
      </Grid>
    </div>
  );
}
